import {
  Activity,
  AlertTriangle,
  BookOpen,
  Brain,
  ChevronRight,
  Database, ExternalLink,
  FlaskConical,
  Gamepad,
  GitBranch,
  HardDrive,
  Layers,
  LogOut,
  MessageSquare,
  Presentation,
  Shield,
  Sparkles,
  Target,
  Terminal,
  ToggleLeft,
  ToggleRight,
  Users,
  Wifi,
  WifiOff,
  X,
  Zap
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PanneauDisk from '../components/SuperAdmin/PanneauDisk';
import PanneauFonctions from '../components/SuperAdmin/PanneauFonctions';
import PanneauInstances from '../components/SuperAdmin/PanneauInstances';
import PanneauMessagerie from '../components/SuperAdmin/PanneauMessagerie';
import PanneauMigrations from '../components/SuperAdmin/PanneauMigrations';
import PanneauModeles from '../components/SuperAdmin/PanneauModeles';
import PanneauRegles from '../components/SuperAdmin/PanneauRegles';
import PanneauStats from '../components/SuperAdmin/PanneauStats';
import { useAuth } from '../contexts/AuthContext';
import monitorSocket from '../socket/monitorSocket';
import usePesacoreIH from '../stores/usePesacore';

const PLATFORM_LINKS = [
  {
    label:  'Parcours',
    sub:    'Bibliothèque des parcours',
    route:  '/parcours',
    icon:   BookOpen,
    style: {
      border:   'border-orange-900/30',
      bg:       'bg-orange-950/10',
      bgHover:  'hover:bg-orange-950/25',
      borderHover: 'hover:border-orange-800/50',
      iconBg:   'bg-orange-950/30',
      iconBorder: 'border-orange-800/30',
      iconColor: 'text-orange-400',
      label:    'text-orange-300',
      ext:      'group-hover:text-orange-500',
    },
  },
  {
      label: "Atlas des Postures",
      sub: 'Cartographie & Scénarios',
      route: '/atlas-posture',
      icon: Users,
      style: {
        border: 'border-amber-900/30',
        bg: 'bg-amber-950/10',
        bgHover: 'hover:bg-amber-950/25',
        borderHover: 'hover:border-amber-800/50',
        iconBg: 'bg-amber-950/30',
        iconBorder: 'border-amber-800/30',
        iconColor: 'text-amber-400',
        label: 'text-amber-300',
        ext: 'group-hover:text-amber-500',
      },
    },
  {
    label:  "Centre d'Ajustement",
    sub:    'Cas d\'études & runs',
    route:  '/centre-ajustement',
    icon:   Target,
    style: {
      border:      'border-indigo-900/30',
      bg:          'bg-indigo-950/10',
      bgHover:     'hover:bg-indigo-950/25',
      borderHover: 'hover:border-indigo-800/50',
      iconBg:      'bg-indigo-950/30',
      iconBorder:  'border-indigo-800/30',
      iconColor:   'text-indigo-400',
      label:       'text-indigo-300',
      ext:         'group-hover:text-indigo-500',
    },
  },
  {
    label:  'Ateliers',
    sub:    'Sessions live & théorie',
    route:  '/ateliers',
    icon:   Sparkles,
    style: {
      border:      'border-pink-900/30',
      bg:          'bg-pink-950/10',
      bgHover:     'hover:bg-pink-950/25',
      borderHover: 'hover:border-pink-800/50',
      iconBg:      'bg-pink-950/30',
      iconBorder:  'border-pink-800/30',
      iconColor:   'text-pink-400',
      label:       'text-pink-300',
      ext:         'group-hover:text-pink-500',
    },
  },
  {
    label:  'Jeux',
    sub:    'Apprentissage & Société',
    route:  '/pesa-exp',
    icon:   Gamepad,
    style: {
      border:      'border-green-900/30',
      bg:          'bg-green-950/10',
      bgHover:     'hover:bg-green-950/25',
      borderHover: 'hover:border-green-800/50',
      iconBg:      'bg-green-950/30',
      iconBorder:  'border-green-800/30',
      iconColor:   'text-green-400',
      label:       'text-green-300',
      ext:         'group-hover:text-green-500',
    },
  },
];

const CREATORS = [
  {
    id: 'parcours',
    badge: 'PARCOURS_CREATOR',
    badgeColor: 'orange',
    title: 'Parcours Creator',
    description: "Construisez des parcours d'apprentissage complets — séquences pédagogiques, processus d'entreprise, chaînes opérationnelles, flux d'onboarding, certifications internes, trajectoires individuelles ou collectives.",
    route: '/admin/parcours-creator',
    outputPath: '/parcours/[slug]',
    meta: { label: 'Validation', value: 'Cross-protocol' },
  },
  {
    id: 'cases',
    badge: 'CASE_CREATOR',
    badgeColor: 'indigo',
    title: 'Case Creator',
    description: "Construisez des simulations avancées — réseaux bayésiens, contrefactuels, calibration génétique, graphes causaux, process mining, projections de bifurcation. Conçu pour l'analyse stratégique et la modélisation de systèmes complexes.",
    route: '/admin/cases-creator',
    outputPath: '/simulateur/[slug]',
    meta: { label: 'Moteur', value: 'Bayesian + Genetic' },
  },
  {
    id: 'ateliers',
    badge: 'ATELIER_CREATOR',
    badgeColor: 'pink',
    title: 'Atelier Creator',
    description: "Créez et gérez les ateliers accessibles aux apprenants — contenu théorique, exercices, ressources, sessions live. Chaque atelier est associé à un template de run via le système de déblocage.",
    route: '/admin/ateliers-creator',
    outputPath: '/ateliers/[slug]',
    meta: { label: 'Déblocage', value: 'Template-based' },
  },
  {
    id: 'commercial',
    badge: 'COMMERCIAL_CREATOR',
    badgeColor: 'blue',
    title: 'Commercial Creator',
    description: "Créez et gérez vos contenus de présentation, arguments de vente, suivis et analyses produits/services.",
    route: '/admin/commercial-creator',
    outputPath: '/simulateur/[slug]',
    meta: { label: 'Moteur', value: 'Bayesian + Genetic' },
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────

function StatusDot({ status, pulse = true }) {
  const colors = {
    healthy:      'bg-emerald-500',
    ready:        'bg-emerald-500',
    warning:      'bg-amber-500',
    degraded:     'bg-amber-500',
    critical:     'bg-red-500',
    error:        'bg-red-500',
    initializing: 'bg-sky-500',
    locked:       'bg-zinc-500',
    offline:      'bg-zinc-600',
  };
  return (
    <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${colors[status] || 'bg-zinc-500'} ${pulse ? 'animate-pulse' : ''}`} />
  );
}

function MetricBar({ value, warn = 70, critical = 85, label }) {
  const v = typeof value === 'number' && isFinite(value) ? value : 0;
  const color = v >= critical ? 'bg-red-500' : v >= warn ? 'bg-amber-500' : 'bg-emerald-500';
  const textColor = v >= critical ? 'text-red-400' : v >= warn ? 'text-amber-400' : 'text-emerald-400';
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 font-bold">{label}</span>
        <span className={`text-[10px] font-black tabular-nums ${textColor}`}>
          {v > 0 ? `${v.toFixed(1)}%` : '—'}
        </span>
      </div>
      <div className="h-px bg-zinc-800 overflow-hidden">
        <div className={`h-full transition-all duration-700 ${color}`} style={{ width: `${Math.min(100, v)}%` }} />
      </div>
    </div>
  );
}

function issueToText(issue) {
  if (!issue) return '';
  if (typeof issue === 'string') return issue;
  if (typeof issue === 'object') {
    const metric = issue.metric ? String(issue.metric).toUpperCase() : '?';
    const val    = typeof issue.value === 'number' ? issue.value.toFixed(1) : String(issue.value ?? '?');
    const suffix = typeof issue.value === 'number' && issue.value <= 100 ? '%' : '';
    return `${metric} à ${val}${suffix}`;
  }
  return String(issue);
}

function issueSeverity(issue) {
  if (typeof issue === 'object' && issue !== null) return issue.level === 'critical' ? 'critical' : 'warn';
  return 'warn';
}

// ─── Panneau : Accueil Dashboard ──────────────────────────────────────────

function PanneauAccueil({ onNavigate }) {
  const {
    kernelStatus, systemHealth, systemMetrics, liveServices,
    totalInstances, totalUsers, pesasGlobalCirculating, criticalAlerts,
    pendingChanges, dryRunMode, socketConnected,
  } = usePesacoreIH();

  const kpis = [
    { label: 'Instances',      value: totalInstances,                                            color: '#38bdf8', icon: Layers        },
    { label: 'Utilisateurs',   value: (totalUsers || 0).toLocaleString(),                        color: '#34d399', icon: Database      },
    { label: 'Circulation ₧',  value: `${((pesasGlobalCirculating || 0) / 1_000).toFixed(2)}K`, color: '#fb923c', icon: Zap           },
    { label: 'Alertes',        value: criticalAlerts,                                            color: criticalAlerts > 0 ? '#f87171' : '#3f3f46', icon: AlertTriangle },
  ];

  return (
    <div className="space-y-8 max-w-6xl">

      {/* Hero */}
      <div className="relative py-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-0 -translate-y-1/2 w-80 h-24 bg-orange-500/5 blur-3xl rounded-full" />
        </div>
        <div className="relative">
          <div className="flex items-baseline gap-4 mb-1">
            <h1 className="text-5xl sm:text-6xl font-black tracking-[-0.04em] text-white">
              PESA<span className="text-orange-500">CORE</span>
            </h1>
            <span className="text-[9px] text-zinc-700 font-bold uppercase tracking-[0.3em] self-end pb-2">_IH · v4.2</span>
          </div>
          <p className="text-[9px] uppercase tracking-[0.35em] text-zinc-700">Kernel Root Interface · Zone Super-Admin</p>
          <div className="flex items-center gap-3 mt-5 flex-wrap">
            <span className="flex items-center gap-2 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-full text-[9px] font-bold uppercase tracking-widest">
              <StatusDot status={kernelStatus} />
              <span className="text-zinc-500">Kernel {kernelStatus}</span>
            </span>
            {socketConnected || monitorSocket.connected ? (
  <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-950/30 border border-emerald-900/40 rounded-full text-emerald-400 text-[9px] font-bold uppercase tracking-widest">
    <Wifi size={9} /> Connected
  </span>
) : (
  <span className="flex items-center gap-2 px-3 py-1.5 bg-zinc-950 border border-zinc-700 rounded-full text-zinc-500 text-[9px] font-bold uppercase tracking-widest">
    <WifiOff size={9} /> Offline
  </span>
)}
            {dryRunMode && (
              <span className="flex items-center gap-2 px-3 py-1.5 bg-amber-950/30 border border-amber-800/40 rounded-full text-amber-400 text-[9px] font-bold uppercase tracking-widest animate-pulse">
                <FlaskConical size={9} /> Dry-run
              </span>
            )}
          </div>
        </div>
      </div>

      {/* KPIs flush grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800/30 border border-zinc-800/30 rounded-xl overflow-hidden">
        {kpis.map(kpi => (
          <div key={kpi.label} className="bg-[#05070a] p-5 sm:p-6 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-[9px] uppercase tracking-[0.22em] text-zinc-700 font-bold">{kpi.label}</span>
              <kpi.icon size={11} style={{ color: kpi.color }} className="opacity-50" />
            </div>
            <div className="text-2xl sm:text-3xl font-black tabular-nums" style={{ color: kpi.color }}>
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* ACCÈS RAPIDE PLATEFORMES                                        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="border border-zinc-800/30 rounded-xl p-5"> 
        <div className="flex items-center gap-2.5 mb-4">
          <ExternalLink size={14} className="text-orange-400" />
          <span className="text-sm font-black uppercase tracking-wider text-orange-300">
            Accès Plateforme
          </span>
          <span className="text-[10px] text-zinc-600 ml-auto font-mono uppercase tracking-widest">
            Vue_Apprenant
          </span>
        </div>
          
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {PLATFORM_LINKS.map(({ label, sub, route, icon: Icon, style }) => (
            <button
              key={route}
              onClick={() => window.open(route, '_blank')}
              className={`group flex items-center gap-4 p-4 border ${style.border} ${style.bg} ${style.bgHover} ${style.borderHover} transition-all text-left`}
            >
              <div className={`p-2.5 border ${style.iconBorder} ${style.iconBg} flex-shrink-0`}>
                <Icon size={16} className={style.iconColor} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-black ${style.label} uppercase tracking-tight`}>
                  {label}
                </div>
                <div className="text-[10px] text-zinc-600 font-mono truncate mt-0.5">
                  {sub}
                </div>
              </div>
            <ExternalLink
              size={12}
              className={`text-zinc-700 ${style.ext} transition-colors flex-shrink-0`}
            />
            </button>
          ))}
        </div>
      </div>

      {/* Santé + Métriques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-zinc-800/50 rounded-xl p-6 space-y-5">
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-700">Santé noyau</span>
            <div className="flex items-center gap-2">
              <StatusDot status={systemHealth.status} />
              <span className="text-[9px] text-zinc-600 uppercase tracking-widest">{systemHealth.status}</span>
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div className={`text-7xl font-black leading-none tabular-nums ${
              (systemHealth.score ?? 100) >= 80 ? 'text-emerald-400' :
              (systemHealth.score ?? 100) >= 55 ? 'text-amber-400' : 'text-red-400'
            }`}>{systemHealth.score ?? '—'}</div>
            <div className="text-zinc-800 text-2xl mb-1 leading-none">/100</div>
          </div>
          {Array.isArray(systemHealth.issues) && systemHealth.issues.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-zinc-900">
              {systemHealth.issues.map((issue, i) => (
                <div key={i} className={`text-[10px] px-3 py-2 rounded font-mono ${
                  issueSeverity(issue) === 'critical'
                    ? 'bg-red-950/20 text-red-400 border-l-2 border-red-600'
                    : 'bg-amber-950/20 text-amber-400 border-l-2 border-amber-600'
                }`}>⚠ {issueToText(issue)}</div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-zinc-800/50 rounded-xl p-6 space-y-5">
          <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-700 block">Métriques live</span>
          <div className="space-y-4">
            <MetricBar value={systemMetrics.cpu}     label="CPU"     warn={70} critical={80} />
            <MetricBar value={systemMetrics.memory}  label="Mémoire" warn={75} critical={85} />
            <MetricBar value={systemMetrics.disk}    label="Disque"  warn={85} critical={92} />
            <MetricBar value={systemMetrics.network} label="Réseau"  warn={60} critical={80} />
          </div>
          <div className="flex justify-between text-[9px] text-zinc-700 pt-3 border-t border-zinc-900">
            <span className="uppercase tracking-widest">Queue depth</span>
            <span className={`font-black ${systemMetrics.queueDepth > 20 ? 'text-amber-400' : 'text-zinc-600'}`}>
              {systemMetrics.queueDepth ?? 0} tâches
            </span>
          </div>
        </div>
      </div>

      {/* Services live */}
      {Array.isArray(liveServices) && liveServices.length > 0 && (
        <div className="border border-zinc-800/50 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-900">
            <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-700">Services live</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-900">
            {liveServices.map((svc, i) => {
              const name   = svc?.name || svc?.id || String(svc ?? `svc-${i}`);
              const status = svc?.status || 'unknown';
              const load   = svc?.load ?? 0;
              return (
                <div key={svc?.id || i} className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <StatusDot status={status} pulse={status !== 'healthy'} />
                    <span className="text-xs text-zinc-400 font-mono">{name}</span>
                  </div>
                  <span className="text-[10px] text-zinc-700 tabular-nums">{load}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pendingChanges > 0 && (
        <div className="border border-amber-900/30 bg-amber-950/10 rounded-xl px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Zap size={13} className="text-amber-400" />
            </div>
            <span className="text-sm text-amber-300 font-bold">{pendingChanges} changement(s) en attente</span>
          </div>
          <button
            onClick={() => onNavigate('migrations')}
            className="text-[9px] uppercase tracking-widest text-amber-500 hover:text-amber-300 font-bold flex items-center gap-1.5 transition-colors"
          >
            Voir migrations <ChevronRight size={11} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Panneau : Sécurité ───────────────────────────────────────────────────

function PanneauSecurite() {
  const { systemAlerts, resolveAlert, ignoreAlert, criticalAlerts } = usePesacoreIH();
  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-black tracking-tighter text-white">NOYAU SÉCURITÉ</h2>
        {criticalAlerts > 0 && (
          <span className="px-3 py-1 bg-red-950/40 border border-red-900/40 rounded-full text-red-400 text-[9px] font-bold uppercase tracking-widest">
            {criticalAlerts} critique(s)
          </span>
        )}
      </div>
      {!Array.isArray(systemAlerts) || systemAlerts.length === 0 ? (
        <div className="text-center py-16 border border-zinc-800/40 rounded-xl">
          <Shield size={28} className="text-emerald-500/20 mx-auto mb-4" />
          <p className="text-zinc-700 text-[10px] uppercase tracking-widest">Aucune alerte — noyau stable</p>
        </div>
      ) : (
        <div className="space-y-2">
          {systemAlerts.map(alert => (
            <div key={alert.id} className={`p-5 rounded-xl border ${
              ['critical', 'emergency'].includes(alert.severity) ? 'bg-red-950/10 border-red-900/30' : 'bg-amber-950/10 border-amber-900/25'
            }`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <AlertTriangle size={11} className={['critical', 'emergency'].includes(alert.severity) ? 'text-red-400' : 'text-amber-400'} />
                    <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                      {String(alert.severity || '')} · {String(alert.type || 'système')}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-300">{String(alert.message || alert.msg || '')}</p>
                  <p className="text-[9px] text-zinc-700 mt-2 font-mono">{String(alert.timestamp || alert.time || '')}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => resolveAlert(alert.id)}
                    className="px-3 py-1.5 bg-emerald-950/20 hover:bg-emerald-950/50 border border-emerald-900/30 rounded text-emerald-400 text-[9px] font-bold uppercase tracking-wider transition-colors">
                    Résoudre
                  </button>
                  <button onClick={() => ignoreAlert(alert.id, 'Ignoré depuis PesacoreIH')}
                    className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded text-zinc-500 text-[9px] font-bold uppercase tracking-wider transition-colors">
                    Ignorer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Panneau : Audit ──────────────────────────────────────────────────────

function PanneauAudit() {
  const { auditLog, refreshAuditLog, engine } = usePesacoreIH();
  const auditSummary = engine?.getAuditSummary?.() || {};
  useEffect(() => { refreshAuditLog(); }, []);
  const levelColors = { success: 'text-emerald-400', failure: 'text-red-400', rejected: 'text-amber-400', 'dry-run': 'text-sky-400' };
  return (
    <div className="space-y-6 max-w-5xl">
      <h2 className="text-xl font-black tracking-tighter text-white">JOURNAL D'AUDIT ROOT</h2>
      {auditSummary.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-800/30 border border-zinc-800/30 rounded-xl overflow-hidden">
          {[
            { label: 'Total',     value: auditSummary.total,    color: 'text-zinc-300'  },
            { label: 'Échecs',    value: auditSummary.failures, color: 'text-red-400'   },
            { label: 'Rejetées', value: auditSummary.rejected, color: 'text-amber-400' },
            { label: 'Dry-runs', value: auditSummary.dryRuns,  color: 'text-sky-400'   },
          ].map(item => (
            <div key={item.label} className="bg-[#05070a] p-5 text-center">
              <div className={`text-2xl font-black ${item.color}`}>{item.value || 0}</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-700 mt-1.5">{item.label}</div>
            </div>
          ))}
        </div>
      )}
      <div className="border border-zinc-800/50 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between">
          <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-700">Actions récentes</span>
          <button onClick={() => refreshAuditLog()} className="text-[9px] text-zinc-700 hover:text-zinc-400 uppercase tracking-widest font-bold transition-colors">Rafraîchir</button>
        </div>
        <div className="divide-y divide-zinc-900 max-h-96 overflow-y-auto">
          {!Array.isArray(auditLog) || auditLog.length === 0 ? (
            <div className="py-10 text-center text-zinc-700 text-[10px] uppercase tracking-widest">Aucune action enregistrée</div>
          ) : (
            auditLog.map(entry => (
              <div key={entry.id} className="px-5 py-3 flex items-start gap-4 hover:bg-zinc-900/20 text-[10px] flex-wrap sm:flex-nowrap transition-colors">
                <span className="text-zinc-700 font-mono whitespace-nowrap shrink-0 tabular-nums">{new Date(entry.timestamp).toLocaleTimeString('fr-FR')}</span>
                <span className={`font-black uppercase tracking-widest shrink-0 w-16 ${levelColors[entry.outcome] || 'text-zinc-600'}`}>{String(entry.outcome || '')}</span>
                <span className="text-zinc-500 font-mono shrink-0">{String(entry.action || '')}</span>
                {entry.notes && <span className="text-zinc-700 truncate">{String(entry.notes)}</span>}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Terminal root ────────────────────────────────────────────────────────

function TerminalRoot({ onClose }) {
  const { systemLogs, systemMetrics, kernelStatus, logsStreaming, toggleLogStream, clearLogs } = usePesacoreIH();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState([
    { text: `Pesacore Kernel v4.2 — ${new Date().toISOString()}`, color: 'text-emerald-500' },
    { text: `Statut : ${kernelStatus}`, color: 'text-zinc-600' },
    { text: 'Tapez "help" pour la liste des commandes.', color: 'text-zinc-700' },
  ]);
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [output]);

  const handleCommand = (e) => {
    if (e.key !== 'Enter' || !input.trim()) return;
    const cmd = input.trim().toLowerCase();
    setInput('');
    const add = (text, color = 'text-zinc-300') => setOutput(prev => [...prev, { text, color }]);
    add(`$ ${cmd}`, 'text-orange-400');
    switch (cmd) {
      case 'help':    add('Commandes : status | metrics | logs | clear | stream', 'text-zinc-600'); break;
      case 'status':  add(`Kernel : ${kernelStatus} | CPU : ${systemMetrics.cpu?.toFixed(1)}% | MEM : ${systemMetrics.memory?.toFixed(1)}%`, 'text-emerald-400'); break;
      case 'metrics': add(`CPU ${systemMetrics.cpu?.toFixed(1)}% | MEM ${systemMetrics.memory?.toFixed(1)}% | DISK ${systemMetrics.disk?.toFixed(1)}% | NET ${systemMetrics.network?.toFixed(1)}%`, 'text-sky-400'); break;
      case 'logs':
        (Array.isArray(systemLogs) ? systemLogs : []).slice(0, 5).forEach(l =>
          add(`[${l.level}] ${l.time || new Date(l.timestamp).toLocaleTimeString('fr-FR')} — ${l.msg}`,
            l.level === 'ERROR' ? 'text-red-400' : l.level === 'WARN' ? 'text-amber-400' : 'text-zinc-500')
        ); break;
      case 'clear':  setOutput([]); break;
      case 'stream': toggleLogStream(); add(`Streaming : ${logsStreaming ? 'DÉSACTIVÉ' : 'ACTIVÉ'}`, 'text-amber-400'); break;
      default:       add(`Commande inconnue : "${cmd}"`, 'text-red-500');
    }
  };

  return (
    <div className="h-56 bg-black border-t border-zinc-900 flex flex-col shrink-0">
      <div className="h-9 bg-zinc-950 border-b border-zinc-900 flex items-center px-4 justify-between shrink-0">
        <span className="flex items-center gap-2.5 text-[9px] text-zinc-700 font-bold uppercase tracking-[0.22em]">
          <Terminal size={10} className="text-orange-500/50" />TERMINAL PESACORE ROOT
        </span>
        <div className="flex items-center gap-3 text-[9px] text-zinc-700 uppercase tracking-widest font-bold">
          <button onClick={clearLogs} className="hover:text-zinc-400 transition-colors">Vider</button>
          <button onClick={onClose} className="hover:text-zinc-200 transition-colors p-0.5"><X size={11} /></button>
        </div>
      </div>
      <div className="flex-1 px-4 py-3 overflow-auto font-mono text-[11px] space-y-0.5 leading-relaxed">
        {output.map((line, i) => <p key={i} className={line.color}>{line.text}</p>)}
        <div ref={bottomRef} />
      </div>
      <div className="h-9 bg-black border-t border-zinc-900 flex items-center px-4 gap-3 shrink-0">
        <span className="text-emerald-700 text-[10px] font-mono shrink-0">root@pesacore:~$</span>
        <input
          className="flex-1 bg-transparent outline-none text-zinc-300 text-[11px] font-mono"
          value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleCommand}
          placeholder="commande..." autoFocus
        />
      </div>
    </div>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'accueil',    label: 'Dashboard',    symbol: '⌂' },
  { id: 'instances',  label: 'Instances',    symbol: '⎈' },
  { id: 'modeles',    label: 'Modèles',      symbol: '⌸' },
  { id: 'fonctions',  label: 'Fonctions',    symbol: '∫' },
  { id: 'migrations', label: 'Migrations',   symbol: '↺' },
  { id: 'regles',     label: 'Règles',       symbol: '⌾' },
  { id: 'securite',   label: 'Sécurité',     symbol: '⎊' },
  { id: 'audit',      label: 'Audit',        symbol: '⍟' },
  { id: 'stats',      label: 'Télémétrie',   symbol: '⤒' },
  { id: 'disque',     label: 'Stockage',     symbol: '⏏' },
];

// ─── Switcher ─────────────────────────────────────────────────────────────

function PanneauSwitcher({ actif, onNavigate }) {
  const map = {
    accueil:    <PanneauAccueil onNavigate={onNavigate} />,
    instances:  <PanneauInstances />,
    securite:   <PanneauSecurite />,
    audit:      <PanneauAudit />,
    modeles:    <PanneauModeles />,
    fonctions:  <PanneauFonctions />,
    migrations: <PanneauMigrations />,
    regles:     <PanneauRegles />,
    stats:      <PanneauStats />,
    disque:     <PanneauDisk />,
  };
  return map[actif] || <PanneauAccueil onNavigate={onNavigate} />;
}

// ─── Atoms ────────────────────────────────────────────────────────────────

const StatusRow = ({ label, value, valueColor }) => (
  <div className="flex justify-between items-center">
    <span className="text-zinc-700 text-[9px] uppercase tracking-[0.2em] font-bold">{label}</span>
    <span className={`font-mono text-[10px] font-black ${valueColor}`}>{String(value)}</span>
  </div>
);

const QuickActionButton = ({ icon: Icon, label, onClick }) => (
  <button onClick={onClick}
    className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-zinc-950 hover:bg-zinc-900 border border-zinc-800/60 hover:border-zinc-700 rounded-lg text-zinc-600 hover:text-zinc-300 transition-all group">
    <Icon size={12} className="group-hover:scale-110 transition-transform" />
    <span className="text-[9px] font-bold uppercase tracking-[0.2em]">{label}</span>
  </button>
);

// ─── Loading & Panic ──────────────────────────────────────────────────────

const LoadingScreen = ({ version }) => (
  <div className="min-h-screen bg-[#000814] flex items-center justify-center font-mono">
    <div className="text-center space-y-8">
      <div className="text-5xl font-black tracking-[-0.04em] text-white">PESA<span className="text-orange-500">CORE</span></div>
      <div className="flex flex-col items-center gap-3">
        <div className="w-40 h-px bg-zinc-900 overflow-hidden">
          <div className="h-full bg-orange-500 animate-[loading_2s_infinite]" />
        </div>
        <p className="text-zinc-700 text-[9px] uppercase tracking-[0.35em]">Initialisation Kernel {version}</p>
      </div>
    </div>
  </div>
);

const KernelPanic = ({ error }) => (
  <div className="min-h-screen bg-[#000814] flex items-center justify-center font-mono p-4">
    <div className="border border-red-900/40 bg-red-950/10 p-10 rounded-xl max-w-md w-full">
      <AlertTriangle size={32} className="text-red-500/70 mb-6" />
      <h2 className="text-red-400 font-black text-lg mb-4 uppercase tracking-tighter">Kernel Panic</h2>
      <div className="bg-black/40 p-4 rounded border border-red-900/20 mb-6">
        <p className="text-red-400/60 text-[10px] font-mono leading-relaxed break-words">{error}</p>
      </div>
      <button onClick={() => window.location.reload()}
        className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-black uppercase tracking-widest transition-all active:scale-95">
        REBOOT SYSTEM
      </button>
    </div>
  </div>
); 

// ─── Composant racine ─────────────────────────────────────────────────────

export default function PesacoreIH_Accueil({ actor }) {
  const {
    initialize, kernelStatus, dryRunMode, toggleDryRun,
    pendingChanges, criticalAlerts, socketConnected,
    loading, error, openMessagerie, closeMessagerie, messagerieOpen,
  } = usePesacoreIH();

  const [panneauActif, setPanneauActif] = useState('accueil');
  const [terminalOuvert, setTerminalOuvert] = useState(false);
  const [mobileDrawer, setMobileDrawer] = useState(false);
  const { logout } = useAuth();

  const startMonitoring = usePesacoreIH(s => s.startMonitoring);
  const stopMonitoring = usePesacoreIH(s => s.stopMonitoring);
  const destroyEngine = usePesacoreIH(s => s.destroyEngine);
  const runHealthCheck = usePesacoreIH(s => s.runHealthCheck);
  const runBackup = usePesacoreIH(s => s.runBackup);
  const flushGlobalCache = usePesacoreIH(s => s.flushGlobalCache);

  const creatorIcons = {
    parcours: GitBranch,
    cases: Brain,
    ateliers: Users,
    commercial: Presentation,
  };

  useEffect(() => {
    initialize(actor);
    startMonitoring();
    return () => { stopMonitoring(); destroyEngine(); };
  }, []);

  const navigate = (id) => {
    setPanneauActif(id);
    setMobileDrawer(false);
    if (messagerieOpen) closeMessagerie();
  };

  const handleOpenMessagerie = () => {
    openMessagerie();
    setMobileDrawer(false);
  };

  const handleLogout = (e) => {
    e.stopPropagation();
    if (window.confirm('CRITICAL: Confirmer la déconnexion du système PESACORE ?')) logout();
  };

  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setTerminalOuvert(p => !p);
      }
      if (e.key === 'Escape' && mobileDrawer) setMobileDrawer(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [mobileDrawer]);
  

  if (loading && kernelStatus === 'initializing') return <LoadingScreen version="v4.2" />;
  if (error && kernelStatus === 'initializing') return <KernelPanic error={error} />;

  const currentSection = SECTIONS.find(s => s.id === panneauActif);

  return ( 
    <div className="min-h-screen h-screen bg-[#020408] text-zinc-200 font-mono flex flex-col select-none">

      {/* HEADER */}
      <header className="h-11 bg-black border-b border-zinc-800 flex items-center px-4 shrink-0 z-50 gap-4">
        {/* Gauche */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="font-black text-sm tracking-[-0.04em] text-white shrink-0">
            PESA<span className="text-orange-400">CORE</span>
            <span className="text-zinc-500 ml-0.5 font-normal text-[10px] tracking-normal">_IH</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-zinc-400 uppercase tracking-widest font-medium">
            <ChevronRight size={10} />
            <span>{currentSection?.label || panneauActif}</span>
          </div>
        </div>

        {/* Centre : status pills */}
        <div className="hidden md:flex items-center gap-2 mx-auto">
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border ${
            kernelStatus === 'ready'
              ? 'bg-emerald-950 border-emerald-700 text-emerald-300'
              : 'bg-amber-950 border-amber-700 text-amber-300'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${kernelStatus === 'ready' ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
            {kernelStatus}
          </div>

          {dryRunMode && (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border bg-amber-950 border-amber-700 text-amber-300 animate-pulse">
              <FlaskConical size={9} /> DRY-RUN
            </div>
          )}

          {pendingChanges > 0 && (
            <button 
              onClick={() => navigate('migrations')}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border bg-sky-950 border-sky-700 text-sky-300 hover:bg-sky-900 transition-colors"
            >
              <GitBranch size={9} />{pendingChanges} pending
            </button>
          )}

          {criticalAlerts > 0 && (
            <button 
              onClick={() => navigate('securite')}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border bg-red-950 border-red-700 text-red-300 hover:bg-red-900 animate-pulse transition-colors"
            >
              <AlertTriangle size={9} />{criticalAlerts} alerte(s)
            </button>
          )}
        </div>

        {/* Droite */}
        <div className="flex items-center gap-1.5 ml-auto shrink-0">
          <div className="hidden sm:flex items-center mr-3">
            {monitorSocket ? (
              <Wifi size={13} className="text-emerald-400" />
            ) : (
              <WifiOff size={13} className="text-zinc-500" />
            )}
          </div>

          <div className="flex items-center gap-px bg-zinc-950 rounded-xl p-1">
            <button
              onClick={toggleDryRun}
              title="Mode dry-run"
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                dryRunMode ? 'bg-amber-500/20 text-amber-400' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900'
              }`}
            >
              {dryRunMode ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
            </button>

            <button
              onClick={handleOpenMessagerie}
              title="Messagerie"
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                messagerieOpen ? 'bg-sky-500/20 text-sky-400' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900'
              }`}
            >
              <MessageSquare size={15} />
            </button>

            <button
              onClick={() => setTerminalOuvert(p => !p)}
              title="Terminal (Ctrl+K)"
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                terminalOuvert ? 'bg-emerald-500/20 text-emerald-400' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900'
              }`}
            >
              <Terminal size={15} />
            </button>

            <div className="w-px h-5 bg-zinc-700 mx-1" />

            <button
              onClick={handleLogout}
              title="Déconnexion"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-950/30 transition-all"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* BODY */}
      <div className="flex flex-1 relative">
        <main className="flex-1 overflow-auto bg-[#05070a] relative lg:pl-20">
          <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
            <div className="absolute top-0 right-1/3 w-80 h-80 bg-orange-500/5 blur-[90px] rounded-full" />
          </div>        

          <div className="relative p-6 lg:p-8 xl:p-10 max-w-7xl mx-auto pb-28 lg:pb-10">
            <PanneauSwitcher actif={panneauActif} onNavigate={navigate} />
          </div>
        </main>

        {/* RIGHT SIDEBAR */}
        <aside className="hidden xl:flex w-56 bg-black border-l border-zinc-800 flex-col shrink-0 overflow-y-auto">
          <div className="flex-1 px-4 py-6 space-y-7">
            {/* ÉTAT */}
            <section>
              <h3 className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-400 mb-4">ÉTAT</h3>
              <div className="space-y-3">
                <StatusRow label="Kernel" value={kernelStatus} valueColor={kernelStatus === 'ready' ? 'text-emerald-400' : 'text-amber-400'} />
                <StatusRow label="Liaison" value={monitorSocket ? 'ONLINE' : 'OFFLINE'} valueColor={monitorSocket ? 'text-emerald-400' : 'text-zinc-400'} />
                <StatusRow label="Mode" value={dryRunMode ? 'SIMULATION' : 'LIVE'} valueColor={dryRunMode ? 'text-amber-400' : 'text-emerald-400'} />
              </div>
            </section>

            {/* OPÉRATIONS */}
            <section className="border-t border-zinc-800 pt-6">
              <h3 className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-400 mb-4">OPÉRATIONS</h3>
              <div className="space-y-1.5">
                <QuickActionButton icon={Activity} label="Health Check" onClick={() => runHealthCheck?.()} />
                <QuickActionButton icon={Zap} label="Flush Cache" onClick={() => flushGlobalCache?.()} />
                <QuickActionButton icon={HardDrive} label="System Backup" onClick={() => runBackup?.()} />
              </div>
            </section>

            {/* BUILDERS */}
            <section className="border-t border-zinc-800 pt-6">
          <h3 className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-400 mb-4">BUILDERS</h3>
          <div className="space-y-1.5">
            {CREATORS.map((creator) => {
              const IconComponent = creatorIcons[creator.id];
              return (
                <Link
                  key={creator.id}
                  to={creator.route}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-medium rounded-lg transition-all bg-zinc-900/50 hover:bg-orange-500/10 border border-transparent hover:border-orange-500/30 text-zinc-300 hover:text-orange-400"
                >
                  {IconComponent && <IconComponent size={14} className="text-zinc-500" />}
                  <span className="truncate">{creator.title}</span>
                  <span className="ml-auto text-[8px] font-mono text-zinc-600">→</span>
                </Link>
              );
            })}
          </div>
        </section>
          </div>

          <div className="px-4 py-4 border-t border-zinc-800">
            <div className="text-[8px] text-zinc-500 font-bold uppercase tracking-tighter">
              AES-256-GCM<br />{typeof window !== 'undefined' ? window.location.hostname : ''}
            </div>
          </div>
        </aside>
      </div>

      {/* NAVIGATION PILL */}
      <nav
        aria-label="Navigation principale"
        className="
            fixed z-[60] pointer-events-none
            /* Positionnement desktop : pill vertical côté gauche */
            lg:left-3 lg:top-[calc(44px+50%)] lg:-translate-y-1/2
            lg:bottom-auto lg:translate-x-0
            /* Positionnement mobile : barre en bas centrée */
            bottom-4 left-1/2 -translate-x-1/2
            lg:left-3 lg:-translate-x-0
        "
        >
        <div className="
            pointer-events-auto
            flex flex-row lg:flex-col
            items-center gap-1 p-1.5
            bg-black/90 backdrop-blur-2xl
            border border-zinc-800/50
            rounded-full
            shadow-[0_4px_24px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.03)]
            max-w-[92vw]                /* ← Ajout important : limite la largeur max sur mobile */
        ">
          {SECTIONS.map((section) => {
            const isActive = panneauActif === section.id;
            return (
              <button
                key={section.id}
                onClick={() => navigate(section.id)}
                title={section.label}
                className={`relative w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 group
                  ${isActive
                    ? 'bg-orange-500 text-black shadow-[0_0_20px_rgba(249,115,22,0.4)]'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800'
                  }`}
              >
                <span className="text-base leading-none">{section.symbol}</span>

                {/* Tooltip desktop */}
                <span className="hidden lg:flex items-center absolute left-[calc(100%+12px)] top-1/2 -translate-y-1/2
                  px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-200 whitespace-nowrap
                  opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 ease-out pointer-events-none z-50"
                >
                  <span className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-y-4 border-y-transparent border-r-4 border-r-zinc-700" />
                  {section.label}
                </span>

                {section.id === 'securite' && criticalAlerts > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse border-2 border-black" />
                )}
                {section.id === 'migrations' && pendingChanges > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full animate-pulse border-2 border-black" />
                )}
              </button>
            );
          })}

          <div className="w-5 h-px lg:w-px lg:h-5 bg-zinc-700 flex-shrink-0" />

          <button
            onClick={handleOpenMessagerie}
            title="Messagerie"
            className={`relative w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 group
              ${messagerieOpen
                ? 'bg-sky-500 text-black shadow-[0_0_20px_rgba(56,189,248,0.35)]'
                : 'text-zinc-400 hover:text-sky-400 hover:bg-zinc-800'
              }`}
          >
            <span className="text-base leading-none">⎚</span>

            <span className="hidden lg:block absolute left-[calc(100%+12px)] top-1/2 -translate-y-1/2
              px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-200 whitespace-nowrap
              opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 ease-out pointer-events-none z-50"
            >
              <span className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-y-4 border-y-transparent border-r-4 border-r-zinc-700" />
              Messagerie
            </span>
          </button>
        </div>
      </nav>

      {/* TERMINAL */}
      {terminalOuvert && <TerminalRoot onClose={() => setTerminalOuvert(false)} />}

      {/* MESSAGERIE */}
      {messagerieOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={closeMessagerie}>
          <div
            className="relative w-full max-w-6xl h-[92vh] bg-[#05070a] border border-zinc-700 rounded-xl overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={closeMessagerie}
              className="absolute top-4 right-4 z-10 p-2 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 rounded-lg transition"
            >
              <X size={15} />
            </button>
            <PanneauMessagerie />
          </div>
        </div>
      )}
    </div>
  );
}